import { useEffect, useMemo, useRef, useState } from 'react';
import { tomtomTypeahead, normalizeSuggestion } from '@api/tomtom';

// Simple debounce
function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function useTomTomAutocomplete({
  query,
  limit = 5,
  language,
  countrySet,
  lat,
  lon,
}) {
  const debounced = useDebounced(query, 300);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const abortRef = useRef(null);

  const options = useMemo(
    () => ({ limit, language, countrySet, lat, lon }),
    [limit, language, countrySet, lat, lon]
  );

  useEffect(() => {
    // empty query → empty list
    if (!debounced || debounced.length < 2) {
      setItems([]);
      setLoading(false);
      setErr(null);
      return;
    }

    setLoading(true);
    setErr(null);

    // abort previous
    if (abortRef.current) abortRef.current.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;

    (async () => {
      try {
        const data = await tomtomTypeahead({
          query: debounced,
          ...options,
          signal: ctl.signal,
        });

        const results = (data?.results || []).map(normalizeSuggestion);
        // eslint-disable-next-line no-console
        console.debug('[TomTom] typeahead results:', results);

        setItems(results);
      } catch (e) {
        if (e.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.error('[TomTom] typeahead error:', e);
          setErr(e);
          setItems([]);
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => ctl.abort();
  }, [debounced, options]);

  return { items, loading, error: err };
}
